import React, { useState, useEffect } from 'react';
import { format } from 'date-fns';
import { useAffaireActiveStatus } from '../hooks/useAffaireActiveStatus';
import {
  XMarkIcon,
  PlayIcon,
  CalendarIcon,
  ClockIcon,
  PauseIcon,
  InformationCircleIcon
} from '@heroicons/react/24/outline';
import '../styles/todo.css';

interface InlineStatusFormProps {
  affaireId: number;
  onClose: () => void;
  onSuccess: () => void;
}

type InactiveMode = 'active' | 'until_date' | 'for_days' | 'until_activity';

const STATUS_OPTIONS: { value: InactiveMode; label: string; description: string; icon: React.ReactNode }[] = [
  {
    value: 'active',
    label: 'Actif',
    description: 'L\'affaire apparaît dans le pipeline',
    icon: <PlayIcon className="w-5 h-5" />
  },
  {
    value: 'until_date',
    label: 'Geler jusqu\'à une date',
    description: 'Restera inactive jusqu\'à la date spécifiée',
    icon: <CalendarIcon className="w-5 h-5" />
  },
  {
    value: 'for_days',
    label: 'Geler pour X jours',
    description: 'Sera gelée pour un nombre de jours',
    icon: <ClockIcon className="w-5 h-5" />
  },
  {
    value: 'until_activity',
    label: 'Désactiver',
    description: 'Réactivée lors de la prochaine activité',
    icon: <PauseIcon className="w-5 h-5" />
  }
];

export const InlineStatusForm: React.FC<InlineStatusFormProps> = ({
  affaireId,
  onClose,
  onSuccess
}) => {
  const {
    status: affaireStatus,
    isLoading: statusLoading,
    setTemporaryInactive,
    setPausedUntilActivity,
    clearManualInactive,
    refetch: refetchStatus
  } = useAffaireActiveStatus(affaireId);

  const [inactiveMode, setInactiveMode] = useState<InactiveMode>('active');
  const [inactiveUntilDate, setInactiveUntilDate] = useState('');
  const [inactiveForDays, setInactiveForDays] = useState('');
  const [inactiveNotes, setInactiveNotes] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Initialize form based on current status
  useEffect(() => {
    if (!affaireStatus) return;

    if (affaireStatus.is_active) {
      setInactiveMode('active');
      setInactiveNotes('');
    } else if (affaireStatus.inactive_reason === 'manual_temporary' && affaireStatus.inactive_until) {
      setInactiveMode('until_date');
      setInactiveUntilDate(format(new Date(affaireStatus.inactive_until), 'yyyy-MM-dd'));
      setInactiveNotes(affaireStatus.inactive_notes || '');
    } else if (affaireStatus.inactive_reason === 'manual_until_activity') {
      setInactiveMode('until_activity');
      setInactiveNotes(affaireStatus.inactive_notes || '');
    }
  }, [affaireStatus]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!affaireStatus) {
      setError('Impossible de charger le statut de l\'affaire');
      return;
    }

    // Validate based on mode
    if (inactiveMode === 'until_date' && !inactiveUntilDate) {
      setError('Veuillez sélectionner une date');
      return;
    }

    if (inactiveMode === 'for_days' && (!inactiveForDays || parseInt(inactiveForDays) < 1)) {
      setError('Veuillez saisir un nombre de jours valide');
      return;
    }

    try {
      setIsProcessing(true);

      const currentInactiveReason = affaireStatus.inactive_reason;
      const isCurrentlyManuallyInactive =
        currentInactiveReason === 'manual_temporary' || currentInactiveReason === 'manual_until_activity';

      if (inactiveMode === 'active') {
        // User wants to activate - clear manual inactive if present
        if (isCurrentlyManuallyInactive) {
          await clearManualInactive();
        }
      } else if (inactiveMode === 'until_date') {
        // Freeze until specific date
        if (inactiveUntilDate) {
          const untilDateTime = new Date(inactiveUntilDate);
          untilDateTime.setHours(23, 59, 59); // End of day
          await setTemporaryInactive({
            inactive_until: untilDateTime.toISOString(),
            notes: inactiveNotes || undefined
          });
        }
      } else if (inactiveMode === 'for_days') {
        // Freeze for X days
        if (inactiveForDays) {
          const days = parseInt(inactiveForDays, 10);
          if (!isNaN(days) && days > 0) {
            const untilDate = new Date();
            untilDate.setDate(untilDate.getDate() + days);
            untilDate.setHours(23, 59, 59);
            await setTemporaryInactive({
              inactive_until: untilDate.toISOString(),
              notes: inactiveNotes || `Gelé pour ${days} jour${days > 1 ? 's' : ''}`
            });
          }
        }
      } else if (inactiveMode === 'until_activity') {
        // Pause until next activity
        await setPausedUntilActivity({
          notes: inactiveNotes || undefined
        });
      }

      // Refetch status to show updated state
      await refetchStatus();

      onSuccess();
      onClose();
    } catch (error: any) {
      console.error('Error updating affaire status:', error);
      setError(error.response?.data?.message || 'Erreur lors de la mise à jour du statut');
    } finally {
      setIsProcessing(false);
    }
  };

  // Check if affaire is permanently inactive (can't be changed)
  const isPermanentlyInactive = affaireStatus?.inactive_reason === 'old_affaire_id';

  return (
    <div className="todo-form">
      {/* Header */}
      <div className="todo-form__header">
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: '12px' }}>
          <h3 className="todo-form__title">Gérer le statut</h3>
          {affaireStatus && (
            <span
              style={{
                padding: '4px 10px',
                borderRadius: '50px',
                fontSize: '12px',
                fontWeight: 600,
                background: affaireStatus.is_active ? '#dcfce7' : '#fef2f2',
                color: affaireStatus.is_active ? '#16a34a' : '#dc2626'
              }}
            >
              {affaireStatus.is_active ? 'Actif' : 'Inactif'}
            </span>
          )}
        </div>
        <button
          className="todo-form__close-btn"
          onClick={onClose}
          type="button"
          disabled={isProcessing}
        >
          <XMarkIcon className="w-5 h-5" />
        </button>
      </div>

      {/* Body */}
      <div className="todo-form__body">
        {/* Show inactive reason if applicable */}
        {affaireStatus && !affaireStatus.is_active && affaireStatus.inactive_reason && (
          <div className="todo-form__info">
            <InformationCircleIcon className="w-5 h-5" style={{ color: '#f59e0b', flexShrink: 0 }} />
            <span>
              {affaireStatus.inactive_reason === 'old_affaire_id' && 'ID trop ancien (< 10000)'}
              {affaireStatus.inactive_reason === 'manual_temporary' && `Gelé jusqu'au ${affaireStatus.inactive_until ? new Date(affaireStatus.inactive_until).toLocaleDateString('fr-BE') : 'N/A'}`}
              {affaireStatus.inactive_reason === 'manual_until_activity' && 'Désactivé (réactivation automatique lors d\'une activité)'}
            </span>
          </div>
        )}

        {/* Show message if permanently inactive */}
        {isPermanentlyInactive && (
          <div className="todo-form__error" style={{ background: '#fef3c7', borderColor: '#fbbf24', color: '#92400e' }}>
            Cette affaire est définitivement inactive (ID &lt; 10000) et ne peut pas être réactivée.
          </div>
        )}

        {/* Status Management Options - Only if not permanently inactive */}
        {!isPermanentlyInactive && (
          <form onSubmit={handleSubmit}>
            {/* Status Option Cards */}
            <div className="todo-form__field">
              <label className="todo-form__label">Modifier le statut</label>
              <div className="todo-status-options">
                {STATUS_OPTIONS.map((option) => (
                  <div
                    key={option.value}
                    className={`todo-status-option ${inactiveMode === option.value ? 'selected' : ''}`}
                    onClick={() => setInactiveMode(option.value)}
                  >
                    <div className="todo-status-icon">
                      {option.icon}
                    </div>
                    <div className="todo-status-content">
                      <div className="todo-status-title">{option.label}</div>
                      <div className="todo-status-description">{option.description}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Input Fields - Show when mode requires input */}
            {inactiveMode !== 'active' && (
              <div className="todo-form__field--row" style={{ marginTop: '16px' }}>
                {/* Date or Days input */}
                {inactiveMode === 'until_date' && (
                  <div className="todo-form__field" style={{ flex: 1 }}>
                    <label className="todo-form__label">Date de fin</label>
                    <input
                      type="date"
                      className="todo-user-select"
                      style={{ paddingLeft: '16px' }}
                      value={inactiveUntilDate}
                      onChange={(e) => setInactiveUntilDate(e.target.value)}
                      min={format(new Date(), 'yyyy-MM-dd')}
                      required
                    />
                  </div>
                )}

                {inactiveMode === 'for_days' && (
                  <div className="todo-form__field" style={{ flex: 1 }}>
                    <label className="todo-form__label">Nombre de jours</label>
                    <input
                      type="number"
                      className="todo-user-select"
                      style={{ paddingLeft: '16px' }}
                      placeholder="Ex: 7"
                      value={inactiveForDays}
                      onChange={(e) => setInactiveForDays(e.target.value)}
                      min="1"
                      max="365"
                      required
                    />
                  </div>
                )}

                {/* Notes */}
                <div className="todo-form__field" style={{ flex: 1 }}>
                  <label className="todo-form__label">Notes</label>
                  <textarea
                    className="todo-textarea"
                    placeholder="Ex: Client en vacances..."
                    value={inactiveNotes}
                    onChange={(e) => setInactiveNotes(e.target.value)}
                    rows={inactiveMode === 'until_activity' ? 3 : 1}
                    style={{ minHeight: inactiveMode === 'until_activity' ? '80px' : '44px' }}
                  />
                </div>
              </div>
            )}

            {/* Error Message */}
            {error && (
              <div className="todo-form__error">
                {error}
              </div>
            )}

            {/* Action Buttons */}
            <div className="todo-form__actions">
              <button
                type="button"
                onClick={onClose}
                disabled={isProcessing}
                className="todo-btn todo-btn--secondary"
              >
                Annuler
              </button>
              <button
                type="submit"
                disabled={isProcessing || statusLoading}
                className="todo-btn todo-btn--primary"
              >
                {isProcessing ? 'Mise à jour...' : 'Enregistrer'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};
